/**
 * Sessão de áudio — runtime não persistível.
 *
 * Aqui ficam as coisas que NÃO podem (ou não devem) ir para o IndexedDB ou
 * para o estado serializável: AudioBuffers montados, dados do instrumental
 * decodificado, adapter ativo e dados de gravação em andamento.
 */
import type { PlayerAdapter } from './audio/player/types.js';
import { AudioEngine, audioEngine } from './audio/AudioEngine.js';
import { PlaybackEngine } from './audio/PlaybackEngine.js';
import { RecorderEngine, recorderEngine } from './audio/RecorderEngine.js';
import { MixerEngine } from './audio/MixerEngine.js';
import { ExportEngine } from './audio/ExportEngine.js';
import { AnalysisEngine, analysisEngine } from './audio/AnalysisEngine.js';
import { TakeEngine, takeEngine } from './audio/TakeEngine.js';
import { syncEngine, SyncEngine } from './audio/SyncEngine.js';
import { projectStorage, ProjectStorageEngine } from './audio/ProjectStorageEngine.js';

export interface InstrumentalData {
  buffer: AudioBuffer;
  left: Float32Array;
  right: Float32Array;
  sampleRate: number;
  duration: number;
  name: string;
}

class Session {
  audio: AudioEngine = audioEngine;
  sync: SyncEngine = syncEngine;
  playback: PlaybackEngine = new PlaybackEngine(audioEngine, syncEngine);
  recorder: RecorderEngine = recorderEngine;
  mixer: MixerEngine = new MixerEngine(audioEngine);
  exporter: ExportEngine = new ExportEngine();
  analysis: AnalysisEngine = analysisEngine;
  takes: TakeEngine = takeEngine;
  storage: ProjectStorageEngine = projectStorage;

  adapter: PlayerAdapter | null = null;
  instrumental: InstrumentalData | null = null;
  /** Float32Array montado por faixa (voz) — fonte de verdade para análise/export. */
  trackData: Map<string, Float32Array> = new Map();
  /** Versão com redução de ruído calculada (não destrutiva). */
  trackDenoised: Map<string, Float32Array> = new Map();
  /** Perfil de ruído capturado (por faixa). */
  noiseProfiles: Map<string, Float32Array> = new Map();
  meterTimer: number | null = null;
  levelTimer: number | null = null;
  countdownTimer: number | null = null;

  get sampleRate(): number {
    return this.audio.sampleRate;
  }

  get duration(): number {
    const a = this.adapter?.getDuration() ?? 0;
    const b = this.instrumental?.duration ?? 0;
    return Math.max(a, b);
  }

  hasReferenceAudio(): boolean {
    return !!this.instrumental;
  }

  clearProjectRuntime() {
    this.trackData.clear();
    this.trackDenoised.clear();
    this.noiseProfiles.clear();
    this.instrumental = null;
  }

  destroyAdapter() {
    try {
      this.adapter?.destroy();
    } catch {
      /* noop */
    }
    this.adapter = null;
    this.playback.setAdapter(null);
  }
}

export const session = new Session();
